import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Shield from 'lucide-react/dist/esm/icons/shield';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import Info from 'lucide-react/dist/esm/icons/info';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import Lightbulb from 'lucide-react/dist/esm/icons/lightbulb';
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { decryptData } from '../../lib/encryption';
import { PasswordSecurityService, SecurityOverview } from '../../lib/passwordSecurityService';
import { Card } from '../common/Card';
import { LoadingSpinner } from '../common/LoadingSpinner';

interface Password {
  id: string;
  name: string;
  password_encrypted?: string;
  encrypted_password?: string;
  password?: string;
  strength?: string;
}

export function SecurityCenter() {
  const { user } = useAuth();
  const [passwords, setPasswords] = useState<Password[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [securityOverview, setSecurityOverview] = useState<SecurityOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadPasswords();
    }
  }, [user]);

  async function loadPasswords() {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('digital_assets')
        .select('*')
        .eq('user_id', user?.id)
        .eq('type', 'password');

      if (fetchError) throw fetchError;
      setPasswords(data || []);
      
      // Calculate security overview
      const overview = PasswordSecurityService.calculateSecurityOverview(data || []);
      setSecurityOverview(overview);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load passwords');
    } finally {
      setLoading(false);
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadPasswords();
    setRefreshing(false);
  };

  // Categorize passwords by strength
  const categorizePasswords = () => {
    const low: Password[] = [];
    const medium: Password[] = [];
    const strong: Password[] = [];

    passwords.forEach((p) => {
      const encryptedPassword =
        p.password_encrypted || p.encrypted_password || p.password || '';
      
      // If we can't decrypt, use stored strength
      if (!encryptedPassword || encryptedPassword.length < 3) {
        const storedStrength = (p.strength || 'medium').toLowerCase();
        if (storedStrength === 'weak' || storedStrength === 'low') {
          low.push(p);
        } else if (storedStrength === 'medium') {
          medium.push(p);
        } else if (storedStrength === 'strong' || storedStrength === 'very_strong') {
          strong.push(p);
        } else {
          medium.push(p);
        }
        return;
      }

      // Calculate strength
      const rating = PasswordSecurityService.calculatePasswordStrength(encryptedPassword.toString());
      
      switch (rating.strength) {
        case 'low':
          low.push(p);
          break;
        case 'medium':
          medium.push(p);
          break;
        case 'strong':
          strong.push(p);
          break;
        default:
          medium.push(p);
      }
    });

    return { low, medium, strong };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const { low: lowPasswords, medium: mediumPasswords, strong: strongPasswords } = categorizePasswords();
  const overallRating = securityOverview
    ? PasswordSecurityService.getOverallRating(securityOverview.overallScore)
    : 'Needs Improvement';

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 70) return 'bg-green-50';
    if (score >= 50) return 'bg-orange-50';
    return 'bg-red-50';
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Security Centre</h1>
          <p className="text-gray-600 mt-1">
            Monitor and improve your password security
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      {/* Overall Security Score Card */}
      {securityOverview && (
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${getScoreBgColor(securityOverview.overallScore)}`}>
                  <Shield
                    size={32}
                    className={getScoreColor(securityOverview.overallScore)}
                  />
                </div>
                <div>
                  <h2 className="text-sm font-medium text-gray-600">Overall Security Score</h2>
                  <p className={`text-2xl font-bold ${getScoreColor(securityOverview.overallScore)}`}>
                    {overallRating}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-4xl font-extrabold ${getScoreColor(securityOverview.overallScore)}`}>
                  {securityOverview.overallScore}
                </div>
                <div className="text-sm text-gray-500">/ 100</div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-4 pt-6 border-t border-gray-200">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {securityOverview.totalPasswords}
                </div>
                <div className="text-sm text-gray-500">Total</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {securityOverview.strongCount}
                </div>
                <div className="text-sm text-gray-500">Strong</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {securityOverview.mediumCount}
                </div>
                <div className="text-sm text-gray-500">Medium</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {securityOverview.lowCount}
                </div>
                <div className="text-sm text-gray-500">Low</div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Password Strength Breakdown */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Password Strength</h2>
        <div className="space-y-4">
          {/* Strong Passwords */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-green-50 rounded-lg">
                      <CheckCircle size={24} className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Strong</h3>
                      <p className="text-sm text-gray-500">
                        {strongPasswords.length} password{strongPasswords.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="px-3 py-1 bg-green-50 rounded-full">
                    <span className="text-sm font-semibold text-green-600">
                      {passwords.length > 0
                        ? Math.round((strongPasswords.length / passwords.length) * 100)
                        : 0}
                      %
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Medium Passwords */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-orange-50 rounded-lg">
                      <Info size={24} className="text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Medium</h3>
                      <p className="text-sm text-gray-500">
                        {mediumPasswords.length} password{mediumPasswords.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="px-3 py-1 bg-orange-50 rounded-full">
                    <span className="text-sm font-semibold text-orange-600">
                      {passwords.length > 0
                        ? Math.round((mediumPasswords.length / passwords.length) * 100)
                        : 0}
                      %
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Low Passwords */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-red-50 rounded-lg">
                      <AlertTriangle size={24} className="text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Low</h3>
                      <p className="text-sm text-gray-500">
                        {lowPasswords.length} password{lowPasswords.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="px-3 py-1 bg-red-50 rounded-full">
                    <span className="text-sm font-semibold text-red-600">
                      {passwords.length > 0
                        ? Math.round((lowPasswords.length / passwords.length) * 100)
                        : 0}
                      %
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Recommendations Section */}
      {securityOverview && securityOverview.recommendations.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recommendations</h2>
          <div className="space-y-3">
            {securityOverview.recommendations.map((recommendation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
              >
                <Card>
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 bg-idecide-primary/10 rounded-lg mt-0.5">
                        <Lightbulb size={20} className="text-idecide-primary" />
                      </div>
                      <p className="text-sm text-gray-700 flex-1">{recommendation}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

